import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Rating, Tooltip } from "@mui/material";

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top-start"
    PopperProps={{
      sx: {
        zIndex: 10000,
      },
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [-20, -10],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

function formatMonthYear(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long" });
}

function formatNumber(num) {
  if (num === null || num === undefined) return "0";
  if (num < 1000) return num.toString();
  if (num < 1_000_000) return (num / 1000).toFixed(num >= 10000 ? 0 : 1) + "K";
  if (num < 1_000_000_000) return (num / 1_000_000).toFixed(1) + "M";
  return (num / 1_000_000_000).toFixed(1) + "B";
}

const UserCard = ({
  user,
  onUsernameClick,
  onFullNameClick,
  children,
  type,
  context = "productPage", // Add context prop with default value
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [cardPosition, setCardPosition] = useState({ top: 40, left: 40 });
  const cardRef = useRef(null);
  const triggerRef = useRef(null);

  // Handle mouse enter on trigger elements
  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    // Calculate position for card context
    if (context === "card" && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const cardWidth = 350;
      const cardHeight = 148;

      // Calculate position with different offsets for avatar and username
      let left = rect.left + (type === "avatar" ? 40 : -8);
      let top = rect.top + 25;

      // Mobile positioning
      if (window.innerWidth <= 856) {
        left = rect.left + (type === "avatar" ? 0 : -56);
        top = rect.top + 48;
      }

      // Ensure card doesn't go off-screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (left < 10) left = 10;
      if (left + cardWidth > viewportWidth - 10) left = viewportWidth - cardWidth - 10;
      if (top + cardHeight > viewportHeight - 10) top = rect.top - cardHeight - 10;

      setCardPosition({ top, left });
    }

    setIsOpen(true);
  };

  // Handle mouse leave with delay
  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsOpen(false);
    }, 200); // Small delay to allow moving to the card
    setHoverTimeout(timeout);
  };

  // Handle mouse enter on the card itself
  const handleCardMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
  };

  // Handle mouse leave from the card
  const handleCardMouseLeave = () => {
    setIsOpen(false);
  };

  // Handle username click
  const handleUsernameClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false); // Close the card
    if (onUsernameClick) {
      onUsernameClick(user);
    }
  };

  // Handle full name click
  const handleFullNameClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false); // Close the card
    if (onFullNameClick) {
      onFullNameClick(user);
    }
  };

  // Handle scroll to close card on mobile/tablet
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
        }
      }
    };

    if (isOpen) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen, hoverTimeout]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  
  return (
    <Box
      ref={triggerRef}
      sx={{
        position: "relative",
        display: "inline-block",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger element (children) */}
      {children}

      {/* User Card */}
      {isOpen && (
        <Box
          ref={cardRef}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
          sx={{
            position: context === "card" ? "fixed" : "absolute",
            width: "350px",
            height: "148px",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            zIndex: 9999,
            p: 2,
            border: "1px solid #F0F0F0",
            top:
              context === "card"
                ? `${cardPosition.top}px`
                : context === "detail" && type === "avatar"
                ? "43px"
                : type === "avatar"
                ? "30px"
                : "28px",
            left:
              context === "card"
                ? `${cardPosition.left}px`
                : context === "detail" && type === "avatar"
                ? "47px"
                : type === "avatar"
                ? "40px"
                : "-16px",
            "@media (max-width: 856px)": {
              top:
                context === "card"
                  ? `${cardPosition.top}px`
                  : context === "detail" && type === "avatar"
                  ? "50px"
                  : context === "detail" && type !== "avatar"
                  ? "35px"
                  : type === "avatar"
                  ? "50px"
                  : "48px",
              left:
                context === "card"
                  ? `${cardPosition.left}px`
                  : context === "detail" && type === "avatar"
                  ? "-25px"
                  : context === "detail" && type !== "avatar"
                  ? "-88px"
                  : type === "avatar"
                  ? "0px"
                  : "-56px",
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              mb: 0.5,
            }}
          >
            <StyledTooltip title="View user's profile page">
              <Typography
                onClick={handleFullNameClick}
                sx={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#333",
                  mb: 0.25,
                  cursor: "pointer",
                  "&:hover": {
                    color: "#7B2CBF",
                    textDecoration: "underline",
                  },
                }}
              >
                {user ? user.firstName + " " + user.lastName : ""}
              </Typography>
            </StyledTooltip>

            <StyledTooltip title="View user's profile page">
              <Typography
                onClick={handleUsernameClick}
                sx={{
                  fontSize: "12px",
                  color: "#666",
                  cursor: "pointer",
                  mb: 0.25,
                  "&:hover": {
                    color: "#7B2CBF",
                    textDecoration: "underline",
                  },
                }}
              >
                @{user.username}
              </Typography>
            </StyledTooltip>

            <Typography
              sx={{
                fontSize: "12px",
                color: "#999",
              }}
            >
              Joined • {formatMonthYear(user.dateJoined)}
            </Typography>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: "#F0F0F0",
              my: 1,
            }}
          />

          {/* Stats */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              textAlign: "center",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#999",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mb: 0.5,
                  fontWeight: "500",
                }}
              >
                LISTINGS
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#333",
                  fontWeight: "600",
                }}
              >
                {user.listings}
              </Typography>
            </Box>

            <Box pl={3}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#999",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mb: 0.5,
                  fontWeight: "500",
                }}
              >
                TOYS SOLD
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#333",
                  fontWeight: "600",
                }}
              >
                {user.soldToys} 
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#999",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mb: 0.5,
                  fontWeight: "500",
                }}
              >
                RATINGS
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Rating
                  value={parseFloat(user.ratingNo) || 0}
                  precision={0.5}
                  readOnly
                  size="small"
                  sx={{
                    "& .MuiRating-iconFilled": {
                      color: "#E53935",
                    },
                    "& .MuiRating-iconEmpty": {
                      color: "#e0e0e0",
                    },
                    "& .MuiRating-icon": {
                      fontSize: "14px",
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#333",
                    fontWeight: "600",
                    ml: 0.5,
                  }}
                >
                  {formatNumber(user.ratingCount)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserCard;
